import React, { useRef } from "react";
import { Input } from "@/components/ui/input";

/**
 * MoneyInput
 * ----------
 * Brazilian currency input that treats typed digits as cents.
 *   type "1"      → R$ 0,01
 *   type "100"    → R$ 1,00
 *   type "10000"  → R$ 100,00
 *   type "100000" → R$ 1.000,00
 *
 * Props:
 *   value       — numeric value (float, e.g. 1200.5) OR string. Can be 0/null/""
 *   onChange    — called with a NUMBER (float), e.g. onChange(1200.5)
 *   placeholder — overrides default ("0,00")
 *   disabled, className, data-testid — passed to <Input>
 */
export default function MoneyInput({ value, onChange, placeholder, disabled, className, "data-testid": testId, ...rest }) {
  const inputRef = useRef(null);

  const numericValue = Number(value);
  const cents = Number.isFinite(numericValue) ? Math.round(numericValue * 100) : 0;

  const formatted =
    cents > 0
      ? (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "";

  const handleChange = (e) => {
    const raw = e.target.value || "";
    // Keep only digits
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      onChange?.(0);
      return;
    }
    // Cap at 15 digits to prevent overflow / absurd inputs
    const safe = digits.slice(0, 15);
    const parsed = parseInt(safe, 10);
    onChange?.(parsed / 100);
  };

  // When user clicks or focuses, place caret at the end so new digits append
  const handleFocus = (e) => {
    requestAnimationFrame(() => {
      try {
        const len = e.target.value.length;
        e.target.setSelectionRange(len, len);
      } catch {}
    });
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={formatted}
      onChange={handleChange}
      onFocus={handleFocus}
      placeholder={placeholder ?? "0,00"}
      disabled={disabled}
      className={className}
      data-testid={testId}
      {...rest}
    />
  );
}
